import Checkbox from '../Checkbox/Checkbox';
import { DateInput } from '../DateInput/DateInput';
import { Icon } from '../Icon/Icon';
import { type Area, type FilterItem } from './Situation.types';
import { type SituationFilterObject } from './SituationPage';

export interface ISituationFilterProp {
    areas: Area[];
    filterState: SituationFilterObject;
    changedFilterCount: number;
    onUpdate: (filterState: SituationFilterObject) => void;
    onReset: () => void;
    loading: boolean;
    region: string;
    className?: string | undefined;
}

const baseFilterItems: FilterItem[] = [
    {
        id: 'road',
        name: 'Vägtrafik',
        icon: 'icon-road',
        subItems: [
            {
                id: 'accidents-and-traffic-messages',
                name: 'Olyckor och hinder',
            },
            {
                id: 'road-works',
                name: 'Vägarbeten',
            },
            {
                id: 'events',
                name: 'Stort evenemang',
            },
            {
                id: 'ferry',
                name: 'Färja',
            },
        ],
    },
    {
        id: 'bicycle',
        name: 'Cykeltrafik',
        icon: 'icon-bicycle',
    },
    {
        id: 'public-transport',
        name: 'Kollektivtrafik',
        icon: 'icon-bus',
    },
    {
        id: 'train',
        name: 'Tågtrafik',
        icon: 'icon-train',
    },
];

const publicTransportSubItems: FilterItem[] = [
    {
        id: 'bus',
        name: 'Buss',
    },
    {
        id: 'tram',
        name: 'Spårvagn',
    },
    {
        id: 'pt-train',
        name: 'Tåg',
    },
    {
        id: 'boat',
        name: 'Båt',
    },
    {
        id: 'pt-other',
        name: 'Övrig trafikinformation',
    },
];

const getFilterItems = (region: string): FilterItem[] =>
    region === 'gbg'
        ? baseFilterItems.map((item) => (item.id === 'public-transport' ? { ...item, subItems: publicTransportSubItems } : item))
        : baseFilterItems;

const SituationListFilter = ({ filterState, areas, onUpdate, onReset, loading, changedFilterCount, region, className }: ISituationFilterProp) => {
    const filterItems = getFilterItems(region);

    const handleChange = (event: React.ChangeEvent<HTMLInputElement>) => {
        const { name: id, checked } = event.target;
        const { checkedFilters } = filterState;
        let newCheckedFilters: string[];
        const filterItem = filterItems.find((item) => item.id === id);
        if (checked) {
            newCheckedFilters = [...checkedFilters, id];
            // Add all subitems when parent is checked
            if (filterItem?.subItems) {
                newCheckedFilters = [...newCheckedFilters, ...filterItem.subItems.map((item) => item.id)];
            }
        } else {
            newCheckedFilters = checkedFilters.filter((x) => x !== id);
            // Uncomment in case we want to remove subitems when parent is unchecked
            // if (!!filterItem?.subItems) {
            //     newCheckedFilters = newCheckedFilters.filter((x) => !filterItem.subItems.map((item) => item.id).includes(x));
            // }
            // Search for parent and uncheck parent if all subitems are unchecked
            filterItems.forEach((item) => {
                if (!!item.subItems && item.subItems.map((subItem) => subItem.id).every((x) => !newCheckedFilters.includes(x))) {
                    newCheckedFilters = newCheckedFilters.filter((x) => x !== item.id);
                }
            });
        }
        onUpdate({ ...filterState, checkedFilters: newCheckedFilters });
    };

    const handleAreaUpdate = (event: React.ChangeEvent<HTMLSelectElement>) => {
        onUpdate({ ...filterState, area: event.target.value });
    };

    const renderFilterItem = (filterItem: FilterItem, disabled?: boolean) => {
        const { id, name, icon, subItems } = filterItem;
        const isChecked = filterState.checkedFilters.includes(id);
        const checkedSubItems = subItems?.length && subItems.filter((item) => filterState.checkedFilters.includes(item.id));
        const indeterminate = subItems?.length && checkedSubItems.length !== subItems.length && checkedSubItems.length > 0;
        const classname = indeterminate ? 'indeterminate' : isChecked ? 'checked' : '';
        const unsupportedByArea = ['public-transport', 'train'].includes(id) && !!filterState.area;

        return (
            <li key={id} className={classname}>
                <Checkbox
                    name={id}
                    label={name}
                    labelIcon={icon}
                    onChange={handleChange}
                    checked={!loading && !indeterminate && isChecked}
                    disabled={loading || disabled || unsupportedByArea}
                    indeterminate={indeterminate}
                />
                {subItems?.length ? (
                    <ul className="traffic-info_subfilters">{subItems.map((item) => renderFilterItem(item, !isChecked || unsupportedByArea))}</ul>
                ) : null}
            </li>
        );
    };

    return (
        <section className={`col-12 situation-page__filter ${className}`} aria-label="Filtreringsval för trafikinformation">
            <form autoComplete="off" className="traffic-info__filter" onSubmit={(e) => e.preventDefault()}>
                <div className="d-flex traffic-info__standard-filter">
                    <label className="visually-hidden" htmlFor="searchtext">
                        Fritextsökning
                    </label>
                    <div className="input-group traffic-info__search">
                        <div className="input-group-text">
                            <Icon symbol="icon-search" className="input-group-icon" />
                        </div>
                        <input
                            id="searchtext"
                            className="form-control"
                            type="search"
                            placeholder="Fritextsökning"
                            name="searchtext"
                            value={filterState.searchText}
                            onChange={(event) => {
                                onUpdate({ ...filterState, searchText: event.target.value });
                            }}
                            onSubmit={(e) => e.preventDefault()}
                            disabled={loading}
                        />
                    </div>
                    <button
                        type="button"
                        className="btn btn-link text-nowrap traffic-info__filter-button collapsed"
                        data-bs-toggle="collapse"
                        data-bs-target="#filter-choices"
                        aria-expanded="false"
                        aria-controls="filter-choices"
                    >
                        {'Fler filter' + (changedFilterCount > 0 ? ' (' + changedFilterCount + ')' : '')}
                        <i className="icon-animated icon-up-open" aria-hidden="true" />
                    </button>
                </div>
                <div id="filter-choices" className="collapse">
                    <div className="traffic-info pt-3">
                        <div className="traffic-info__filter-list">
                            <label className="traffic-info__heading" htmlFor="area-select">
                                Område:
                            </label>
                            <select
                                id="area-select"
                                className="form-control mb-1"
                                onChange={handleAreaUpdate}
                                value={filterState.area}
                                disabled={loading}
                                aria-describedby="area-select-caption"
                            >
                                <option key="default" value="">
                                    - Välj område -
                                </option>
                                {areas.map(
                                    (area, index) =>
                                        area.area !== undefined && (
                                            <option className="dropdown-item" key={index + area.area} value={area.area}>
                                                {area.name}
                                            </option>
                                        ),
                                )}
                            </select>
                            <div id="area-select-caption" className="traffic-info__small-info mt-0 mb-3">
                                Områdesfiltrering fungerar endast för väg- och cykeltrafik
                            </div>
                            <div className="traffic-info__heading">Urval:</div>
                            <ul>{filterItems.map((item) => renderFilterItem(item))}</ul>
                            <hr className="traffic-info__filter_divider" />
                            <div className="traffic-info__date">
                                <div className="traffic-info__heading traffic-info__date-heading">Datumintervall:</div>

                                <div className="traffic-info__date-range">
                                    <div className="traffic-info__date-field">
                                        <label className="traffic-info__label" htmlFor="datefrom">
                                            Från:
                                        </label>
                                        <DateInput
                                            className="form-control"
                                            id="datefrom"
                                            name="datefrom"
                                            value={filterState.dateFrom}
                                            max={filterState.dateTo}
                                            onChange={(date) => onUpdate({ ...filterState, dateFrom: date })}
                                            disabled={loading}
                                        />
                                    </div>
                                    <div className="traffic-info__date-field">
                                        <label className="traffic-info__label" htmlFor="dateto">
                                            Till:
                                        </label>
                                        <DateInput
                                            className="form-control"
                                            id="dateto"
                                            name="dateto"
                                            value={filterState.dateTo}
                                            min={filterState.dateFrom}
                                            onChange={(date) => onUpdate({ ...filterState, dateTo: date })}
                                            disabled={loading}
                                        />
                                    </div>
                                    <div className="traffic-info__small-info traffic-info__date-info">
                                        (Visar händelser som är aktiva mellan valda datum)
                                    </div>
                                    <hr className="traffic-info__date-divider" />
                                    <button
                                        type="button"
                                        className="btn btn-dark btn-block traffic-info__cancel traffic-info__date-reset"
                                        onClick={onReset}
                                    >
                                        <i className="icon-cancel" aria-hidden="true" />
                                        Återställ standardval
                                    </button>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            </form>
        </section>
    );
};

export { SituationListFilter };
